import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router';
import QRCode from 'react-qr-code';
import { Button, Box } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import QRCodeIcon from '~Assets/icons/qr-scan.svg';
import { useButton, useI18n, useLogger } from '~Hooks';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import useModal from '~ComponentsImproved/Modal/useModal';
import { CREATE_LOGIN_LINK, GET_LOGIN_LINK } from '../../api';
import { CreateLoginLink, GetLoginLink, GetLoginLinkVariables } from '../../../Login/__queries__';
import { useStorage } from '~Components/Storage';

const QRWrapper = styled.div`
  background: var(--colorWhite);
  border-radius: 8px;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  min-width: 200px;
`;

const Code = styled.span<{ marginRight?: boolean }>`
  font-family: monospace;
  display: inline-block;
  margin-right: ${(props) => (props.marginRight ? '12px' : '4px')};
  font-size: 20px;
  padding: 4px 8px;
  background: var(--colorBlueGray1100);
  border-radius: 4px;
  border: 1px solid var(--colorBlueGray800);
  color: var(--colorWhite);
`;

const shimmer = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const HeaderBox = styled(Box)`
  text-align: center;
`;

const SkeletonLine = styled.div<{ width: string; height: string }>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    var(--colorBlueGray1100) 25%,
    var(--colorBlueGray800) 50%,
    var(--colorBlueGray1100) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2.5s infinite ease-in-out;
  ${shimmer}
`;

const SkeletonCode = styled.div`
  display: inline-block;
  width: 28px;
  height: 32px;
  margin-right: 4px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    var(--colorBlueGray1100) 25%,
    var(--colorBlueGray800) 50%,
    var(--colorBlueGray1100) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2.5s infinite ease-in-out;
  ${shimmer}

  &:nth-of-type(3),
  &:nth-of-type(6) {
    margin-right: 12px;
  }
`;

const CenteredBox = styled(Box)`
  text-align: center;
`;

interface Props {
  onToken: (token: string) => void;
}

const QRLoginButton: FC<Props> = ({ onToken }) => {
  const { i18n } = useI18n();
  const [_, commitToStorage] = useStorage();
  const logger = useLogger('login');

  const qrModal = useModal();

  /**
   * Auto-open the modal when the URL contains `?isTV=true` so TV devices deep-link
   * straight into the QR login experience and automatically allow browser clicks.
   * The effect only re-runs when the param transitions to `true`, so closing the
   * modal does not immediately re-open it.
   */
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isTV = searchParams.get('isTV') === 'true';
  const isUsingBrowser = !window.ReactNativeWebView;

  useEffect(() => {
    if (isTV) {
      logger.info('Auto-opening QR login modal & applying allowBrowserClick setting for TV');

      if (isUsingBrowser) {
        commitToStorage({ allowBrowserClick: true });
      }

      qrModal.show();
    }
  }, [isTV]);

  const [createLoginLink] = useMutation<CreateLoginLink>(CREATE_LOGIN_LINK);
  const [loginLinkCode, setLoginLinkCode] = useState<string | null>(null);
  const [linkError, setLinkError] = useState(false);

  // Create login link when modal opens
  useEffect(() => {
    if (!qrModal.isShowing) return;

    logger.info('Creating login link for QR code');

    createLoginLink().then(({ data, errors }) => {
      const loginLinkId = data?.createLoginLink?.id;

      if ((errors && errors.length > 0) || !loginLinkId) {
        logger.error('Failed to create login link', { errors });
        setLinkError(true);
        return;
      }

      logger.info('Login link created', { loginLinkId });
      setLoginLinkCode(loginLinkId);
    });

    // Reset state when modal closes
    return () => {
      setLoginLinkCode(null);
      setLinkError(false);
    };
  }, [qrModal.isShowing]);

  // Poll for login link token
  const loginLinkQuery = useQuery<GetLoginLink, GetLoginLinkVariables>(GET_LOGIN_LINK, {
    skip: !loginLinkCode || !qrModal.isShowing,
    variables: { id: loginLinkCode! },
    pollInterval: 3000,
  });

  // Handle token received from QR login
  useEffect(() => {
    if (!loginLinkQuery.data) return;

    const token = loginLinkQuery.data.LoginLink?.token;
    console.log('loginLInkQuery: ', loginLinkQuery.data);
    if (!token) return;

    logger.info('QR login token received');
    qrModal.hide();
    onToken(token);
  }, [loginLinkQuery.data]);

  const QRUrl = `admin.${
    window.__DEV__ ? 'development.' : ''
  }baemingo.com/link?code=${loginLinkCode}`;

  const qrButton = useButton({
    onClick: () => {
      logger.info('QR code login clicked');
      qrModal.show();
    },
  });

  return (
    <>
      <Button
        type="submit"
        fluid
        display="flex"
        gap="xs"
        justifyContent="center"
        alignItems="center"
        {...qrButton}
        size="small">
        <Icon src={QRCodeIcon} color="var(--colorBlack)" height="16px" width="16px" />
        {i18n('Login.SigninQR')}
      </Button>

      <Modal isShowing={qrModal.isShowing} backdropClose hide={qrModal.hide}>
        <ModalWrapper width="420px">
          <HeaderBox display="flex" flexDirection="column" gap="xs">
            <Heading level={2} color="var(--colorWhite)">
              {i18n('Login.LogInViaAnotherDevice')}
            </Heading>

            <Paragraph level={5} color="var(--colorBlueGray200)">
              {i18n('Login.ScanTheQRCode')}
            </Paragraph>
          </HeaderBox>

          <QRWrapper>
            {linkError ? (
              <Paragraph level={3} color="var(--colorUIRed)">
                {i18n('Login.CreateLinkFail')}
              </Paragraph>
            ) : loginLinkCode ? (
              <QRCode
                value={QRUrl}
                fgColor="var(--colorBlueGray1300)"
                style={{ display: 'block' }}
                size={180}
              />
            ) : (
              <Loader size="md" />
            )}
          </QRWrapper>

          <CenteredBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="xs">
            {loginLinkCode ? (
              <>
                <Paragraph level={4} color="var(--colorBlueGray300)">
                  {i18n('Login.OrGoTo')}{' '}
                  <strong style={{ color: 'var(--colorWhite)' }}>
                    https://admin.baemingo.com/link
                  </strong>{' '}
                  {i18n('Login.EnterTheCode')}:
                </Paragraph>
                <Box display="flex" justifyContent="center">
                  {loginLinkCode.split('').map((c, i) => (
                    <Code marginRight={i === 2 || i === 5} key={i}>
                      {c}
                    </Code>
                  ))}
                </Box>
              </>
            ) : (
              <>
                <SkeletonLine width="260px" height="16px" />
                <Box display="flex" justifyContent="center">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <SkeletonCode key={i} />
                  ))}
                </Box>
              </>
            )}
          </CenteredBox>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default QRLoginButton;
